import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const PILL_LABELS = [
  { id: 'latest', label_en: 'Now', label_kn: 'ಇದೀಗ' },
  { id: 'trending', label_en: 'Trend', label_kn: 'ಟ್ರೆಂಡ್' },
  { id: 'featured', label_en: 'Special', label_kn: 'ವಿಶೇಷ' },
];

/**
 * Standardised section header used on Home, Category and listing pages.
 * - White rounded-top tab with coloured drop shadow + accent bar.
 * - Three mini filter pills (Latest / Trending / Featured) - optional.
 * - Optional "More" link on the right.
 */
export default function SectionHeader({
  title,
  catId,
  accent = '#d32f2f',
  activeTab,
  onTabChange,
  showPills = true,
  showMore = true,
  moreHref,
}) {
  const { isKannada } = useLanguage();
  const href = moreHref || (catId ? `/category/${catId}` : null);

  return (
    <div className="mb-5" data-testid={`section-header-${catId || 'mixed'}`}>
      <div className="flex items-end gap-3 flex-wrap">
        {/* Elevated main tab */}
        <div className="relative">
          <div
            className="bg-white rounded-t-xl px-5 py-3 border border-b-0 border-gray-100 flex items-center gap-2 -mb-px"
            style={{ boxShadow: `0 6px 18px -8px ${accent}55` }}
          >
            <span className="w-1 h-5 rounded-full" style={{ backgroundColor: accent }} />
            <h2 className="text-[22px] font-black text-[#1a1a1a] font-kannada leading-none">{title}</h2>
          </div>
          <div className="h-0.5 rounded" style={{ backgroundColor: accent }} />
        </div>

        {/* Mini filter pills */}
        {showPills && onTabChange && (
          <div className="flex items-center gap-1 pb-1" data-testid={`section-pills-${catId || 'mixed'}`}>
            {PILL_LABELS.map(t => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTabChange?.(t.id)}
                  className={`px-3 py-1 text-[11px] font-bold rounded-full transition-all ${
                    isActive ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:text-[#1a1a1a]'
                  }`}
                  style={isActive ? { backgroundColor: accent, boxShadow: `0 2px 6px -1px ${accent}80` } : undefined}
                  data-testid={`section-pill-${catId || 'mixed'}-${t.id}`}
                >
                  {isKannada ? t.label_kn : t.label_en}
                </button>
              );
            })}
          </div>
        )}

        {showMore && href && (
          <Link
            to={href}
            className="ml-auto text-[13px] font-bold hover:underline flex items-center gap-0.5 pb-1"
            style={{ color: accent }}
            data-testid={`more-${catId || 'all'}`}
          >
            {isKannada ? 'ಇನ್ನಷ್ಟು' : 'More'} <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
